"use client";

import { useState, type ReactNode } from "react";
import { useRouter } from "next/navigation";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@ocr-monorepo/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@ocr-monorepo/ui/components/dialog";
import { Input } from "@ocr-monorepo/ui/components/input";
import { Label } from "@ocr-monorepo/ui/components/label";
import { useTRPC } from "@/lib/trpc";
import { toast } from "sonner";
import { publicErrorMessage } from "@ocr-monorepo/shared";

export function CreateConverterDialog({ trigger }: { trigger: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const router = useRouter();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const createConversion = useMutation(
    trpc.converters.create.mutationOptions({
      onSuccess: ({ conversion }) => {
        void queryClient.invalidateQueries({
          queryKey: trpc.converters.list.queryKey({ type: "pdf_to_csv" }),
        });
        setName("");
        setOpen(false);
        router.push(`/dashboard/convert/pdf-to-csv?conversion=${conversion.id}`);
      },
      onError: (error) =>
        toast.error("Could not create converter", {
          description: publicErrorMessage("system_error"),
        }),
    }),
  );
  const projectName = name.trim();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (projectName) {
              createConversion.mutate({
                type: "pdf_to_csv",
                name: projectName,
              });
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Create converter</DialogTitle>
            <DialogDescription>
              Name this project so you can find it in your conversion history.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 py-5">
            <Label htmlFor="converter-project-name">Project name</Label>
            <Input
              id="converter-project-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="January vendor invoices"
              autoFocus
              maxLength={120}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!projectName || createConversion.isPending}
            >
              {createConversion.isPending ? "Creating…" : "Create converter"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
